import { ErrorBoundary } from '@datadog/browser-rum-react'

import styles from './ErrorFallback.module.css'

/**
 * Full-page fallback rendered when a render error escapes to the app boundary.
 *
 * Deliberately self-contained so it stays renderable even when the tree below it
 * has failed. Offers a reload as the single recovery action rather than `resetError`,
 * since a top-level crash usually means the app state is no longer trustworthy.
 *
 * Names Regard rather than an app for that same reason.
 */
const ErrorFallback = () => (
  <main className={styles.container}>
    <div className={styles.card}>
      <h1 className={styles.heading}>Something went wrong</h1>
      <p className={styles.message}>
        Regard hit an unexpected error. Reloading usually fixes it. Contact
        Regard support if the problem continues.
      </p>
      <button
        className={styles.button}
        onClick={() => window.location.reload()}
        type="button"
      >
        Reload
      </button>
    </div>
  </main>
)

/**
 * App-level error boundary that reports React render errors to Datadog RUM.
 *
 * Datadog's `ErrorBoundary` reports caught errors via `addReactError`, so they
 * land in RUM with the same identity/global context as the rest of the session.
 * Reporting is a safe no-op when RUM was never initialized, so this boundary is
 * always mounted regardless of config.
 *
 * Wrap the app's `RouterProvider` in `App.tsx` with this; it is the single
 * top-level boundary, so any uncaught render error surfaces the reload fallback.
 */
export const AppErrorBoundary = ErrorBoundary

export { ErrorFallback }
